import { Fragment, useState } from "react";

import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { IconActionDeleteOutline } from "@/assets/icons/figma/IconActionDeleteOutline";
import { IconActionLogOut } from "@/assets/icons/figma/IconActionLogOut";
import { AccountMenuItem } from "@/common/contracts/account.contracts";
import { useLazyNamespace } from "@/common/hooks/i18n/useLazyNamespace";
import {
  ACCOUNT_LAYOUT_ITEMS,
  ACCOUNT_MENU_GROUPS,
  ACCOUNT_MENU_TOP_ITEMS,
} from "@/common/static/account.static";
import { ACCOUNT_ADS_ROUTE } from "@/common/static/routes.static";
import { cn } from "@/common/utils/css.utils";
import { AccountLayoutMenuListItem } from "@/components/Account/common/AccountLayoutMenuListItem";
import { AccountLayoutUserInfo } from "@/components/Account/common/AccountLayoutUserInfo";
import { getActiveAccountMenuPath } from "@/components/Account/common/accountMenu.utils";
import { useAccountMenuItemVisibility } from "@/components/Account/common/hooks/useAccountMenuItemVisibility";
import { showAccountDeleteModal } from "@/components/Account/modals/delete-account/AccountDeleteModal";
import { showAccountLogoutModal } from "@/components/Account/modals/logout/AccountLogoutModal";
import { MegaMenuMobileBack } from "@/components/mega-menu/mobile/MegaMenuMobileBack";
import { MegaMenuMobileClose } from "@/components/mega-menu/mobile/MegaMenuMobileClose";
import { dashboardIndicatorStore } from "@/features/account-dashboard/data/dashboard.store";
import { STRIPE_SETTINGS_PATH } from "@/features/account-dashboard/utils/dashboard.utils";
import { OpenHomeMenuBadge } from "@/features/open-home/account/OpenHomeMenuBadge";
import { useOpenHomeMenuEntry } from "@/features/open-home/account/open-home-account";
import { ProProjectsMenuNewLabel } from "@/features/pro-buyer-projects/components/ProProjectsMenuNewLabel";
import { Heading } from "@/ui/cva/Heading";
import {
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogTrigger,
} from "@radix-ui/react-dialog";

type FooterActionProps = {
  icon: JSX.Element;
  label: string;
  danger?: boolean;
  onClick: () => void;
};

const FooterAction = ({ icon, label, danger, onClick }: FooterActionProps) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full cursor-pointer items-center space-x-2 rounded-xl border border-transparent px-4 py-2.5 text-left font-body text-base",
        danger
          ? "text-attention-500 hover:bg-attention-50"
          : "text-black hover:bg-flavour-50 hover:text-brand-500"
      )}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
};

type MenuItemProps = {
  item: AccountMenuItem;
  active: boolean;
  /** WHO-5531: small Attention dot, used while a Stripe verification is open. */
  indicator?: boolean;
  onClick: () => void;
  /** Open Home: offers waiting, shown next to the label. */
  count?: number;
};

/**
 * One drawer row. Same label resolution and the same Attention dot as the
 * desktop sidebar's MenuItem, so both menus read identically (WHO-5531 F8).
 */
const MenuItem = ({
  item,
  active,
  indicator,
  onClick,
  count,
}: MenuItemProps) => {
  const { t } = useTranslation();
  const label = item.labelFallback
    ? t(item.label, item.labelFallback)
    : t(item.label);

  return (
    <li>
      <AccountLayoutMenuListItem
        path={item.path}
        active={active}
        onClick={onClick}
      >
        <span className="flex w-full items-center space-x-2">
          <span className="relative">
            <item.icon className="h-6 w-6" />
            {indicator && (
              <span
                role="img"
                data-testid="account-menu-indicator"
                aria-label={t(
                  "account-dashboard:dashboard-menu-attention",
                  "Actie nodig"
                )}
                className="absolute -right-0.5 -top-0.5 block size-2.5 rounded-full border-2 border-white bg-attention-500"
              />
            )}
          </span>
          <span>{label}</span>
          {item.proBuyerProjectsOnly && <ProProjectsMenuNewLabel />}
          {count !== undefined && <OpenHomeMenuBadge count={count} />}
        </span>
      </AccountLayoutMenuListItem>
    </li>
  );
};

type AccountMenuMobilePanelProps = {
  /** Opens the drawer. Rendered as-is through Radix `asChild`. */
  trigger: JSX.Element;
  /**
   * Set when the drawer is stacked on another mobile panel (the hamburger):
   * shows the back chevron and hides this content while the parent animates
   * out, the same trick MegaMenuMobilePanel uses for nested dialogs.
   */
  hasBack?: boolean;
  /** Close-all callback, run after a navigation or a footer action. */
  onClose?: () => void;
};

/**
 * WHO-5453 round 4, F15. The grouped account menu for phones: below `md` the
 * sidebar is not rendered, so the same five groups, the same labels and the
 * Log out / Delete account footer live in this full-screen drawer instead.
 * Used by the account layout (trigger bar under the breadcrumbs) and by the
 * hamburger menu in place of the old flat Account submenu.
 *
 * WHO-5531 round 2 (F8): mirrors the sidebar completely, so the ungrouped
 * Dashboard item sits above the groups and Betaalinstellingen carries the
 * Attention dot while a Stripe verification is open.
 */
export const AccountMenuMobilePanel = ({
  trigger,
  hasBack,
  onClose,
}: AccountMenuMobilePanelProps) => {
  const { t } = useTranslation();
  const { asPath } = useRouter();
  const [open, setOpen] = useState(false);
  const [isContentHidden, setIsContentHidden] = useState(false);
  const hasStripeVerificationAction = dashboardIndicatorStore(
    state => state.hasStripeVerificationAction
  );
  // The hamburger renders this drawer on pages that never loaded the
  // `account` namespace (homepage, PDP, search); without this the labels are
  // raw keys. Account pages already have the bundle, so this is a no-op there.
  const translationsReady = useLazyNamespace("account");
  const isMenuItemVisible = useAccountMenuItemVisibility();
  useLazyNamespace("open-home");
  const openHome = useOpenHomeMenuEntry();

  const activePath = getActiveAccountMenuPath(
    asPath,
    ACCOUNT_LAYOUT_ITEMS.map(item => item.path)
  );

  const closeAll = () => {
    if (hasBack) {
      setIsContentHidden(true);
    }

    setOpen(false);
    onClose?.();
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);

    if (next) {
      setIsContentHidden(false);
    }
  };

  const runFooterAction = (action: () => void) => () => {
    closeAll();
    action();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent
          aria-label={t("account:my-account")}
          className={cn(
            "fixed inset-0 z-[1000] flex flex-col space-y-6 bg-white p-4 data-[state=closed]:animate-menu-mobile-hide data-[state=open]:animate-menu-mobile-show",
            isContentHidden && "invisible"
          )}
        >
          <div className="flex items-center space-x-2 border-b border-b-grey-300 pb-4">
            {hasBack && <MegaMenuMobileBack />}
            <Heading as="span" size="h4" className="flex-1">
              {translationsReady ? t("account:my-account") : ""}
            </Heading>
            <MegaMenuMobileClose onClick={closeAll} />
          </div>
          <nav
            aria-label={t("account:my-account")}
            className="flex flex-1 flex-col overflow-y-auto overflow-x-hidden"
          >
            {translationsReady && (
              <>
                {/* WHO-5954: the sidebar's welcome block, phones only. */}
                <AccountLayoutUserInfo className="px-4 pb-6" />
                {ACCOUNT_MENU_TOP_ITEMS.length > 0 && (
                  <ul className="flex flex-col space-y-1 pb-6">
                    {ACCOUNT_MENU_TOP_ITEMS.map(item => (
                      <MenuItem
                        key={item.path}
                        item={item}
                        active={item.path === activePath}
                        onClick={closeAll}
                      />
                    ))}
                  </ul>
                )}
                {ACCOUNT_MENU_GROUPS.map((group, index) => (
                  <Fragment key={group.id}>
                    <span
                      className={cn(
                        "px-4 pb-2 font-title text-xs font-bold uppercase tracking-wide text-grey-500",
                        index > 0 && "pt-6"
                      )}
                    >
                      {t(group.title, group.titleFallback)}
                    </span>
                    <ul className="flex flex-col space-y-1">
                      {group.items.filter(isMenuItemVisible).map(item => (
                        <Fragment key={item.path}>
                          <MenuItem
                            item={item}
                            active={item.path === activePath}
                            indicator={
                              item.path === STRIPE_SETTINGS_PATH &&
                              hasStripeVerificationAction
                            }
                            onClick={closeAll}
                          />
                          {item.path === ACCOUNT_ADS_ROUTE && openHome && (
                            <MenuItem
                              item={openHome.item}
                              active={false}
                              count={openHome.count}
                              onClick={closeAll}
                            />
                          )}
                        </Fragment>
                      ))}
                    </ul>
                  </Fragment>
                ))}
                <hr className="my-6 border-grey-200" />
                <div className="flex flex-col space-y-1 pb-4">
                  <FooterAction
                    icon={<IconActionLogOut className="h-6 w-6" />}
                    label={t("common:common-log-out")}
                    onClick={runFooterAction(showAccountLogoutModal)}
                  />
                  <FooterAction
                    icon={<IconActionDeleteOutline className="h-6 w-6" />}
                    label={t("account:account-settings-delete-account")}
                    danger
                    onClick={runFooterAction(showAccountDeleteModal)}
                  />
                </div>
              </>
            )}
          </nav>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};
